import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

function AdminAnaliticas() {
  const [pedidos, setPedidos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [rango, setRango] = useState("7");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const cargarPedidos = async () => {
    setCargando(true);
    setMensaje("");

    try {
      const snapshot = await getDocs(collection(db, "pedidos"));

      const lista = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPedidos(lista);
    } catch (error) {
      console.error("Error cargando analíticas:", error);
      setMensaje("❌ No se pudieron cargar las analíticas.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, []);

  const obtenerFechaPedido = (pedido) => {
    if (!pedido.fecha) return null;

    const fecha = new Date(pedido.fecha);

    if (Number.isNaN(fecha.getTime())) return null;

    return fecha;
  };

  const estaDentroDelRango = (pedido) => {
    const fechaPedido = obtenerFechaPedido(pedido);

    if (!fechaPedido) return false;

    const ahora = new Date();

    if (rango === "todo") {
      return true;
    }

    if (rango === "hoy") {
      return (
        fechaPedido.getFullYear() === ahora.getFullYear() &&
        fechaPedido.getMonth() === ahora.getMonth() &&
        fechaPedido.getDate() === ahora.getDate()
      );
    }

    if (rango === "mes") {
      return (
        fechaPedido.getFullYear() === ahora.getFullYear() &&
        fechaPedido.getMonth() === ahora.getMonth()
      );
    }

    if (rango === "personalizado") {
      if (!fechaInicio || !fechaFin) return true;

      const inicio = new Date(`${fechaInicio}T00:00:00`);
      const fin = new Date(`${fechaFin}T23:59:59`);

      return fechaPedido >= inicio && fechaPedido <= fin;
    }

    const dias = Number(rango);

    if (!dias || Number.isNaN(dias)) return true;

    const limite = new Date();

    limite.setDate(limite.getDate() - dias);

    return fechaPedido >= limite;
  };

  const formatoDinero = (valor) => {
    return `$${Number(valor || 0).toFixed(2)} MXN`;
  };

  const formatoFecha = (fechaISO) => {
    const fecha = new Date(fechaISO);

    if (Number.isNaN(fecha.getTime())) return "Sin fecha";

    return fecha.toLocaleDateString("es-MX", {
      timeZone: "America/Mexico_City",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const analiticas = useMemo(() => {
    const pedidosFiltrados = pedidos
      .filter(estaDentroDelRango)
      .filter((pedido) => pedido.estatus !== "cancelado");

    const totalPedidos = pedidosFiltrados.length;

    const ventaTotal = pedidosFiltrados.reduce(
      (acc, pedido) => acc + Number(pedido.totalFinal || 0),
      0,
    );

    const totalComida = pedidosFiltrados.reduce(
      (acc, pedido) => acc + Number(pedido.totalComida || 0),
      0,
    );

    const totalEnvio = pedidosFiltrados.reduce(
      (acc, pedido) => acc + Number(pedido.costoEnvio || 0),
      0,
    );

    const ticketPromedio = totalPedidos > 0 ? ventaTotal / totalPedidos : 0;

    const pagos = {};
    const entregas = {};
    const productos = {};
    const ventasPorDia = {};
    const clientes = {};

    pedidosFiltrados.forEach((pedido) => {
      const metodoPago = pedido.metodoPago || "No especificado";
      const tipoEntrega = pedido.tipoEntrega || "No especificado";
      const fechaTexto = formatoFecha(pedido.fecha);
      const cliente = pedido.clienteEmail || "Sin correo";

      if (!pagos[metodoPago]) {
        pagos[metodoPago] = {
          cantidad: 0,
          total: 0,
        };
      }

      pagos[metodoPago].cantidad += 1;
      pagos[metodoPago].total += Number(pedido.totalFinal || 0);

      if (!entregas[tipoEntrega]) {
        entregas[tipoEntrega] = {
          cantidad: 0,
          total: 0,
        };
      }

      entregas[tipoEntrega].cantidad += 1;
      entregas[tipoEntrega].total += Number(pedido.totalFinal || 0);

      if (!ventasPorDia[fechaTexto]) {
        ventasPorDia[fechaTexto] = {
          cantidad: 0,
          total: 0,
        };
      }

      ventasPorDia[fechaTexto].cantidad += 1;
      ventasPorDia[fechaTexto].total += Number(pedido.totalFinal || 0);

      if (!clientes[cliente]) {
        clientes[cliente] = {
          cantidad: 0,
          total: 0,
        };
      }

      clientes[cliente].cantidad += 1;
      clientes[cliente].total += Number(pedido.totalFinal || 0);

      (pedido.productos || []).forEach((producto) => {
        const nombreProducto = producto.nombre || "Producto sin nombre";
        const cantidad = Number(producto.cantidad || 1);
        const precio = Number(producto.precio || 0);

        if (!productos[nombreProducto]) {
          productos[nombreProducto] = {
            nombre: nombreProducto,
            cantidad: 0,
            total: 0,
          };
        }

        productos[nombreProducto].cantidad += cantidad;
        productos[nombreProducto].total += cantidad * precio;
      });
    });

    const listaPagos = Object.entries(pagos)
      .map(([nombre, data]) => ({
        nombre,
        ...data,
      }))
      .sort((a, b) => b.total - a.total);

    const listaEntregas = Object.entries(entregas)
      .map(([nombre, data]) => ({
        nombre,
        ...data,
      }))
      .sort((a, b) => b.total - a.total);

    const listaProductos = Object.values(productos).sort(
      (a, b) => b.cantidad - a.cantidad,
    );

    const listaVentasPorDia = Object.entries(ventasPorDia)
      .map(([fecha, data]) => ({
        fecha,
        ...data,
      }))
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    const listaClientes = Object.entries(clientes)
      .map(([email, data]) => ({
        email,
        ...data,
      }))
      .sort((a, b) => b.total - a.total);

    return {
      pedidosFiltrados,
      totalPedidos,
      ventaTotal,
      totalComida,
      totalEnvio,
      ticketPromedio,
      listaPagos,
      listaEntregas,
      listaProductos,
      listaVentasPorDia,
      listaClientes,
    };
  }, [pedidos, rango, fechaInicio, fechaFin]);

  const tarjeta = (titulo, valor, subtitulo) => (
    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid #eee",
        borderRadius: "16px",
        padding: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          color: "#666",
          fontWeight: "700",
          marginBottom: "6px",
        }}
      >
        {titulo}
      </div>

      <div
        style={{
          fontSize: "24px",
          fontWeight: "900",
          color: "#111",
        }}
      >
        {valor}
      </div>

      {subtitulo && (
        <div
          style={{
            fontSize: "12px",
            color: "#777",
            marginTop: "4px",
          }}
        >
          {subtitulo}
        </div>
      )}
    </div>
  );

  const tablaSimple = (titulo, columnas, filas, renderFila) => (
    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid #eee",
        borderRadius: "16px",
        padding: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        overflowX: "auto",
      }}
    >
      <h3
        style={{
          margin: "0 0 12px 0",
          fontSize: "17px",
          fontWeight: "900",
        }}
      >
        {titulo}
      </h3>

      {filas.length === 0 ? (
        <p style={{ color: "#777", fontSize: "13px" }}>
          No hay información para este periodo.
        </p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              {columnas.map((columna) => (
                <th
                  key={columna}
                  style={{
                    textAlign: "left",
                    borderBottom: "1px solid #eee",
                    padding: "8px",
                    color: "#555",
                  }}
                >
                  {columna}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>{filas.map(renderFila)}</tbody>
        </table>
      )}
    </div>
  );

  return (
    <div>
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #eee",
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "18px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        }}
      >
        <h2
          style={{
            margin: "0 0 8px 0",
            fontSize: "22px",
            fontWeight: "900",
          }}
        >
          📊 Analíticas de ventas
        </h2>

        <p
          style={{
            margin: "0 0 16px 0",
            color: "#666",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          Consulta el comportamiento de ventas, pedidos, métodos de pago,
          productos más vendidos y clientes recurrentes.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "10px",
          }}
        >
          <label
            style={{
              fontSize: "13px",
              fontWeight: "800",
            }}
          >
            Periodo de análisis
          </label>

          <select
            value={rango}
            onChange={(e) => setRango(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid #ddd",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          >
            <option value="hoy">Hoy</option>
            <option value="7">Últimos 7 días</option>
            <option value="30">Últimos 30 días</option>
            <option value="mes">Este mes</option>
            <option value="todo">Todo</option>
            <option value="personalizado">Rango personalizado</option>
          </select>

          {rango === "personalizado" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={{
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />

              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                style={{
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}

          <button
            type="button"
            onClick={cargarPedidos}
            disabled={cargando}
            style={{
              backgroundColor: "#111",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "12px",
              fontSize: "14px",
              fontWeight: "800",
              cursor: cargando ? "not-allowed" : "pointer",
            }}
          >
            {cargando ? "Actualizando..." : "Actualizar analíticas"}
          </button>
        </div>
      </div>

      {mensaje && (
        <div
          style={{
            padding: "12px",
            borderRadius: "8px",
            fontSize: "14px",
            marginBottom: "20px",
            fontWeight: "500",
            backgroundColor: "#fce8e6",
            color: "#c5221f",
          }}
        >
          {mensaje}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "12px",
          marginBottom: "18px",
        }}
      >
        {tarjeta("Ventas registradas", formatoDinero(analiticas.ventaTotal))}
        {tarjeta("Pedidos", analiticas.totalPedidos)}
        {tarjeta("Ticket promedio", formatoDinero(analiticas.ticketPromedio))}
        {tarjeta("Total comida", formatoDinero(analiticas.totalComida))}
        {tarjeta("Total envío", formatoDinero(analiticas.totalEnvio))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "18px",
        }}
      >
        {tablaSimple(
          "🍔 Productos más vendidos",
          ["Producto", "Cantidad", "Venta estimada"],
          analiticas.listaProductos.slice(0, 10),
          (producto) => (
            <tr key={producto.nombre}>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {producto.nombre}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {producto.cantidad}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {formatoDinero(producto.total)}
              </td>
            </tr>
          ),
        )}

        {tablaSimple(
          "💳 Ventas por método de pago",
          ["Método", "Pedidos", "Total"],
          analiticas.listaPagos,
          (pago) => (
            <tr key={pago.nombre}>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {pago.nombre}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {pago.cantidad}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {formatoDinero(pago.total)}
              </td>
            </tr>
          ),
        )}

        {tablaSimple(
          "🚚 Ventas por tipo de entrega",
          ["Entrega", "Pedidos", "Total"],
          analiticas.listaEntregas,
          (entrega) => (
            <tr key={entrega.nombre}>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {entrega.nombre}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {entrega.cantidad}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {formatoDinero(entrega.total)}
              </td>
            </tr>
          ),
        )}

        {tablaSimple(
          "📅 Ventas por día",
          ["Fecha", "Pedidos", "Total"],
          analiticas.listaVentasPorDia,
          (dia) => (
            <tr key={dia.fecha}>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {dia.fecha}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {dia.cantidad}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {formatoDinero(dia.total)}
              </td>
            </tr>
          ),
        )}

        {tablaSimple(
          "👤 Clientes con más pedidos",
          ["Cliente", "Pedidos", "Total"],
          analiticas.listaClientes.slice(0, 10),
          (cliente) => (
            <tr key={cliente.email}>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {cliente.email}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {cliente.cantidad}
              </td>
              <td style={{ padding: "8px", borderBottom: "1px solid #f1f1f1" }}>
                {formatoDinero(cliente.total)}
              </td>
            </tr>
          ),
        )}
      </div>
    </div>
  );
}

export default AdminAnaliticas;
